import { ShellSnapshotLoader } from "@heydex/client-runtime/state/shell";
import type { PreparedConnection } from "@heydex/client-runtime/connection";
import * as Effect from "effect/Effect";
import * as Exit from "effect/Exit";
import * as Cause from "effect/Cause";
import { safeErrorLogAttributes } from "@heydex/client-runtime/errors";
import * as Option from "effect/Option";
import { FetchHttpClient } from "effect/http";

import { resolveInitialPrimaryEnvironmentDescriptor } from "./context";
import { primaryHttpBaseUrl } from "./target";

function startBootRequests() {
  const httpBaseUrl = primaryHttpBaseUrl();
  const descriptor = resolveInitialPrimaryEnvironmentDescriptor();
  const shell = Effect.runPromiseExit(
    ShellSnapshotLoader.fetchPrimaryEnvironmentShellSnapshot(httpBaseUrl).pipe(
      Effect.provide(FetchHttpClient.layer),
    ),
  );
  // Discovery can reject before its connection source mounts to observe it.
  void descriptor.catch((error) => {
    console.warn(
      "Could not discover the primary environment during boot.",
      safeErrorLogAttributes(error),
    );
  });
  return { httpBaseUrl, descriptor, shell };
}

let boot: ReturnType<typeof startBootRequests> | undefined;
let started = false;

/** Called only by the application graph, after the reader identity gate. */
export function startPrimaryBoot(): void {
  if (!started) {
    boot = startBootRequests();
    started = true;
  }
}

export function takeBootShellSnapshot(prepared: PreparedConnection) {
  if (boot === undefined || prepared.httpBaseUrl !== boot.httpBaseUrl) return undefined;
  const pending = boot;
  // A reconnect must fetch a fresh authoritative snapshot, even if its cursor is valid.
  boot = undefined;
  return Effect.promise(() => pending.shell).pipe(
    Effect.flatMap((exit) =>
      Exit.isSuccess(exit)
        ? Effect.succeed(Option.some(exit.value))
        : Effect.logWarning("Could not load the boot shell snapshot; requesting it over RPC.").pipe(
            Effect.annotateLogs({ ...safeErrorLogAttributes(Cause.squash(exit.cause)) }),
            Effect.as(Option.none()),
          ),
    ),
  );
}
